import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { requestHandler } from '../utils/requestHandler';
import {
  FEATURES_ENDPOINT,
  CONTROL_CENTER_DEFAULT_FEATURE_FLAGS,
} from '../utils/constants/controlCenterConstants';
import {
  isMock,
  setErrorInAppState,
  convertStringToLowerCase,
} from '../utils/helpers';
import { mockFeatureResponse } from '../mocks/mockFeatureResponse';
import { AppState, FeatureSlice } from '../interfaces/store.interface';

export const initialState: FeatureSlice = {
  data: {},
  loadApp: false,
};

export const feature = 'feature';

export const fetchFeatures = createAsyncThunk(
  `${feature}/fetchFeatures`,
  async () => {
    try {
      if (isMock()) {
        return mockFeatureResponse.data;
      } else {
        const { data }: any = await requestHandler({
          customHeaders: {
            'Content-Type': 'application/json',
          },
          skipAuthToken: true,
        }).get(FEATURES_ENDPOINT);
        return data;
      }
    } catch (error: any) {
      const errorObject = error?.response || error;
      setErrorInAppState(errorObject, 'features');
      return error;
    }
  },
);

export const featureSlice = createSlice({
  name: feature,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // NOTE: https://redux-toolkit.js.org/api/createSlice#extrareducers
    builder.addCase(fetchFeatures.pending, (state) => {
      state.loadApp = false;
    });
    builder.addCase(fetchFeatures.fulfilled, (state, { payload }: any) => {
      state.loadApp = true;
      state.data = payload;
    });
    builder.addCase(fetchFeatures.rejected, (state) => {
      state.loadApp = true; // this can be flipped to false to show error page
      state.data = {}; // later on the error can be posted in this case
    });
  },
});

export default featureSlice.reducer;

// state getters can be written & exported here
export const getStaticFeatures = (state: AppState) => state.feature.data;
export const getSelectedClientFeature = (
  state: AppState,
  selectedClientId = '',
) => {
  // when selectedClientId is not passed to this getter,
  // assume the getter is invoked without a selected client context
  // use the lead clientId from session in this case
  const clientId = selectedClientId ? selectedClientId : state.session.clientId;
  return (
    state.feature.data.find((clientFeature: any) => {
      const featureClient = convertStringToLowerCase(clientFeature.client);
      const selectedClient = convertStringToLowerCase(clientId);
      return featureClient === selectedClient;
    }) || {
      // set all default features to false incase client not present
      features: CONTROL_CENTER_DEFAULT_FEATURE_FLAGS,
    }
  );
};
